/** payables · deep-link de aprovação (spec 01 §10 · spec 03 §9). */
import { z } from 'zod'
import { codigoDeMoeda, dataNominal, uuid } from './comum.js'
import { dinheiroPositivo } from './contas-pagar.js'
import { referenciaDeFicha } from './referencias.js'

export const tipoDeAlvoDeAprovacao = z.enum(['parcela_pagar', 'pagamento_rh', 'parcela_comissao'])
export type TipoDeAlvoDeAprovacao = z.infer<typeof tipoDeAlvoDeAprovacao>

export const estadoDeLinkDeAprovacao = z.enum(['valido', 'expirado', 'consumido'])
export type EstadoDeLinkDeAprovacao = z.infer<typeof estadoDeLinkDeAprovacao>

export const linkDeAprovacaoResolvido = z.object({
  alvoTipo: tipoDeAlvoDeAprovacao,
  alvoId: uuid,
  estado: estadoDeLinkDeAprovacao,
})
export type LinkDeAprovacaoResolvido = z.infer<typeof linkDeAprovacaoResolvido>

export const decisaoDeDesembolso = z.enum(['aprovada', 'rejeitada'])
export type DecisaoDeDesembolso = z.infer<typeof decisaoDeDesembolso>

export const alcadaDeDesembolso = z.object({
  id: uuid,
  nome: z.string(),
  valorAte: z.string().nullable(),
  nivelAcessoId: uuid,
  nivelAcessoNome: z.string(),
  exigeSegundaAprovacao: z.boolean(),
  ordem: z.number().int().positive(),
})
export type AlcadaDeDesembolso = z.infer<typeof alcadaDeDesembolso>

/**
 * A alçada como a 10.1 a ADMINISTRA — a de leitura da 6.5, mais o que só quem
 * administra precisa ver.
 *
 * A fila de aprovação pergunta "qual faixa vale para este valor, nesta entidade, agora"
 * e por isso só enxerga a ATIVA, já resolvida. Quem administra pergunta "que faixas
 * existem", e precisa da desativada e do ALCANCE de cada uma: `entidadeId` nulo é a
 * faixa que vale para todas as entidades, que é como a seed do 7.3 nasce.
 */
export const alcadaAdministrada = alcadaDeDesembolso.extend({
  ativa: z.boolean(),
  entidadeId: uuid.nullable(),
  entidadeNome: z.string().nullable(),
})
export type AlcadaAdministrada = z.infer<typeof alcadaAdministrada>

/**
 * O que a 10.1 grava numa faixa (spec 03 §9 · DS-15).
 *
 * `valorAte` nulo é a faixa do TOPO: sem teto, aprova qualquer valor. Vazio e zero não
 * são a mesma coisa, e por isso o campo é anulável em vez de "0 = sem limite" — o banco
 * cobra o mesmo (`CHECK (valor_ate IS NULL OR valor_ate > 0)`).
 */
export const entradaDeAlcada = z.object({
  nome: z.string().trim().min(1, 'Informe o nome da alçada.'),
  valorAte: dinheiroPositivo.nullable(),
  nivelAcessoId: uuid,
  exigeSegundaAprovacao: z.boolean(),
  ordem: z.number().int().positive('A ordem da faixa começa em 1.'),
  ativa: z.boolean(),
  entidadeId: uuid.nullable(),
})
export type EntradaDeAlcada = z.infer<typeof entradaDeAlcada>

export const itemPendenteDeAprovacao = z.object({
  alvoTipo: tipoDeAlvoDeAprovacao,
  alvoId: uuid,
  ciclo: z.number().int().positive(),
  contaPagarId: uuid.nullable(),
  codigo: z.string(),
  favorecidoNome: z.string(),
  /**
   * Quem recebe, com ficha (D-136, DS-538): o fornecedor da CAP (nulo no avulso), a Empresa
   * ou o Contato do parceiro da comissão, e no RH a PJ contratada ou o colaborador (nulo na
   * modalidade PJ sem a empresa vinculada, cuja pendência é texto).
   */
  favorecido: referenciaDeFicha.nullable(),
  /** O registro que o código abre: a CAP, o pedido da comissão ou o colaborador do RH. */
  registro: referenciaDeFicha.nullable(),
  descricao: z.string(),
  registradoPorId: uuid.nullable(),
  registradoPorNome: z.string(),
  vencimento: dataNominal,
  naFilaDesde: z.string(),
  agingDias: z.number().int().nonnegative(),
  aguardandoMaisDe48h: z.boolean(),
  alcadaId: uuid,
  alcadaNome: z.string(),
  valor: z.string(),
  moeda: codigoDeMoeda,
  podeDecidir: z.boolean(),
  conflitoInteresse: z.boolean(),
  acimaDaAlcada: z.boolean(),
  jaDecidiu: z.boolean(),
  exigeSegundaAprovacao: z.boolean(),
})
export type ItemPendenteDeAprovacao = z.infer<typeof itemPendenteDeAprovacao>

export const decisaoDeDesembolsoRegistrada = z.object({
  id: uuid,
  alvoTipo: tipoDeAlvoDeAprovacao,
  alvoId: uuid,
  ciclo: z.number().int().positive(),
  codigo: z.string(),
  favorecidoNome: z.string(),
  /** Os mesmos dois de `itemPendenteDeAprovacao`, pelas mesmas junções (D-136, DS-538). */
  favorecido: referenciaDeFicha.nullable(),
  registro: referenciaDeFicha.nullable(),
  decisao: decisaoDeDesembolso,
  motivoNome: z.string().nullable(),
  motivoObs: z.string().nullable(),
  aprovadorNome: z.string(),
  quando: z.string(),
  alcadaNome: z.string(),
  valor: z.string(),
  moeda: codigoDeMoeda,
})
export type DecisaoDeDesembolsoRegistrada = z.infer<typeof decisaoDeDesembolsoRegistrada>

export const respostaDaFilaDeAprovacoes = z.object({
  data: z.array(itemPendenteDeAprovacao),
  meta: z.object({
    alcadas: z.array(alcadaDeDesembolso),
    alcadasDoUsuario: z.array(alcadaDeDesembolso),
    nivelNome: z.string(),
    podeAprovar: z.boolean(),
    agregados: z.object({
      naSuaFila: z.number().int().nonnegative(),
      valorNaSuaFila: z.string(),
      aguardandoMaisDe48h: z.number().int().nonnegative(),
      /** Nome legado: conta pendências nas faixas cujo valorAte é null, independentemente do nome. */
      diretoria: z.number().int().nonnegative(),
      aprovadasHoje: z.number().int().nonnegative(),
      valorAprovadoHoje: z.string(),
    }),
  }),
})
export type RespostaDaFilaDeAprovacoes = z.infer<typeof respostaDaFilaDeAprovacoes>

export const respostaDoHistoricoDeAprovacoes = z.object({
  data: z.array(decisaoDeDesembolsoRegistrada),
})
export type RespostaDoHistoricoDeAprovacoes = z.infer<typeof respostaDoHistoricoDeAprovacoes>

export const entradaDeRejeicaoDeDesembolso = z.object({
  motivoId: uuid,
  observacao: z.string().trim().min(1).max(2000).optional(),
})
export type EntradaDeRejeicaoDeDesembolso = z.infer<typeof entradaDeRejeicaoDeDesembolso>

export const resultadoDaDecisaoDeDesembolso = z.object({
  id: uuid,
  alvoId: uuid,
  estado: z.enum(['aprovada', 'aguardando_segunda_aprovacao', 'rejeitada']),
})
export type ResultadoDaDecisaoDeDesembolso = z.infer<typeof resultadoDaDecisaoDeDesembolso>
